/**
 * Custom hook for consent management
 * 
 * This hook encapsulates all the common logic for consent management
 * between AuthPage and GuestPage, reducing code duplication and
 * providing a consistent interface for consent operations.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { createConsentApiService, ConsentApiService, UserInfo, TokenData } from '../utils/ConsentApiService';
import { LoadingState, CONSENT_CONFIG, VALIDATION_MESSAGES } from '../config/consentConfig';
import { ConsentErrorHandler, ValidationError } from '../utils/errors';

// Hook state interface
interface ConsentState {
  hasConsented: boolean;
  consentDecision: 'accept' | 'decline' | null;
  consentText: string;
  validationError: string | null;
  tokenData: TokenData | null;
  initializationState: LoadingState;
  consentSubmissionState: LoadingState;
}

// Hook return interface
interface UseConsentManagementReturn {
  // State
  state: ConsentState;
  apiService: ConsentApiService | null;
  
  // Computed values
  isSubmitDisabled: boolean;
  isAcceptEnabled: boolean;
  requiredConsentText: string;
  
  // Actions
  handleConsentDecisionChange: (decision: 'accept' | 'decline') => void;
  handleConsentTextChange: (text: string) => void;
  handleSubmit: () => Promise<void>;
  clearValidationError: () => void;
  
  // Initialization
  initialize: () => Promise<void>;
}

// Hook parameters
interface UseConsentManagementParams {
  userType: 'guest' | 'authenticated';
  userInfo?: UserInfo;
  requireTextInput?: boolean;
}

export const useConsentManagement = ({
  userType,
  userInfo,
  requireTextInput = false
}: UseConsentManagementParams): UseConsentManagementReturn => {
  
  // State management
  const [state, setState] = useState<ConsentState>({
    hasConsented: false,
    consentDecision: null,
    consentText: '',
    validationError: null,
    tokenData: null,
    initializationState: LoadingState.LOADING,
    consentSubmissionState: LoadingState.IDLE
  });

  const [apiService, setApiService] = useState<ConsentApiService | null>(null);

  // Memoized constants
  const requiredConsentText = useMemo(() => CONSENT_CONFIG.REQUIRED_TEXT, []);

  // Initialize API service
  const initialize = useCallback(async () => {
    setState(prev => ({ ...prev, initializationState: LoadingState.LOADING }));
    
    try {
      const service = createConsentApiService(userType, userInfo);
      const result = await service.initialize();

      if (result.isValid) {
        setApiService(service);
        setState(prev => ({
          ...prev,
          tokenData: result.tokenData || null,
          validationError: null,
          initializationState: LoadingState.SUCCESS
        }));
      } else {
        setState(prev => ({
          ...prev,
          validationError: result.errorMessage || VALIDATION_MESSAGES.INITIALIZATION_ERROR_GUEST,
          initializationState: LoadingState.ERROR
        }));
      }
    } catch (error) {
      const errorResult = ConsentErrorHandler.handleApiError(error, 'Service Initialization');
      setState(prev => ({
        ...prev,
        validationError: errorResult.error,
        initializationState: LoadingState.ERROR
      }));
    }
  }, [userType, userInfo]);

  // Initialize on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Handle consent decision change
  const handleConsentDecisionChange = useCallback((decision: 'accept' | 'decline') => {
    setState(prev => ({
      ...prev,
      consentDecision: decision,
      validationError: null // Clear validation error when user makes a selection
    }));
  }, []);

  // Handle consent text change
  const handleConsentTextChange = useCallback((text: string) => {
    setState(prev => ({
      ...prev,
      consentText: text,
      validationError: null // Clear validation error when user starts typing
    }));
  }, []);

  // Clear validation error
  const clearValidationError = useCallback(() => {
    setState(prev => ({ ...prev, validationError: null }));
  }, []);

  // Handle consent submission
  const handleSubmit = useCallback(async () => {
    // Prevent multiple submissions
    if (state.consentSubmissionState === LoadingState.LOADING) {
      return;
    }

    try {
      // Validate form data
      ConsentErrorHandler.validateConsentSubmission(
        state.consentDecision,
        requireTextInput ? state.consentText : undefined,
        requireTextInput ? requiredConsentText : undefined
      );

      if (!apiService) {
        throw new ValidationError(VALIDATION_MESSAGES.SERVICE_NOT_INITIALIZED);
      }

      setState(prev => ({
        ...prev,
        consentSubmissionState: LoadingState.LOADING,
        validationError: null
      }));

      // Prepare submission data
      const interviewUpdateData = {
        useCase: CONSENT_CONFIG.USE_CASE,
        entityName: CONSENT_CONFIG.ENTITY_NAME,
        customObjectId: state.tokenData?.interviewId,
        requesterId: userType === 'guest' 
          ? CONSENT_CONFIG.DEFAULT_REQUESTER_ID 
          : userInfo?.alias || CONSENT_CONFIG.DEFAULT_REQUESTER_ID,
        requesterType: userType === 'guest' 
          ? CONSENT_CONFIG.REQUESTER_TYPE_SYSTEM_USER 
          : CONSENT_CONFIG.REQUESTER_TYPE_AGENT,
        customObjectFields: JSON.stringify({
          'intervieweeConsentGiven': state.consentDecision === 'accept',
          'intervieweeConsentGivenBy': CONSENT_CONFIG.CONSENT_PORTAL_SOURCE
        })
      };

      // Submit consent with retry mechanism
      const result = await ConsentErrorHandler.withRetry(async () => {
        return apiService.submitConsent({
          consentData: interviewUpdateData,
          submissionType: userType === 'guest' ? 'Guest' : 'Authenticated',
          decision: state.consentDecision!
        });
      });

      if (result.success) {
        setState(prev => ({
          ...prev,
          hasConsented: true,
          consentSubmissionState: LoadingState.SUCCESS
        }));
      } else {
        setState(prev => ({
          ...prev,
          validationError: result.error || VALIDATION_MESSAGES.SUBMISSION_ERROR,
          consentSubmissionState: LoadingState.ERROR
        }));
      }

    } catch (error) {
      const errorResult = ConsentErrorHandler.handleApiError(error, 'Consent Submission');
      setState(prev => ({
        ...prev,
        validationError: errorResult.error,
        consentSubmissionState: LoadingState.ERROR
      }));
    }
  }, [
    state.consentDecision,
    state.consentText,
    state.consentSubmissionState,
    state.tokenData,
    apiService,
    userType,
    userInfo,
    requireTextInput,
    requiredConsentText
  ]);

  // Computed values
  const isAcceptEnabled = useMemo(() => {
    if (!requireTextInput) return true;
    return state.consentText?.trim()?.toLowerCase() === requiredConsentText.toLowerCase() && 
           state.consentSubmissionState !== LoadingState.LOADING;
  }, [state.consentText, requiredConsentText, state.consentSubmissionState, requireTextInput]);

  const isSubmitDisabled = useMemo(() => {
    return !state.consentDecision || 
           state.consentSubmissionState === LoadingState.LOADING || 
           (state.consentDecision === 'accept' && requireTextInput && !isAcceptEnabled);
  }, [state.consentDecision, state.consentSubmissionState, isAcceptEnabled, requireTextInput]);

  return {
    state,
    apiService,
    isSubmitDisabled,
    isAcceptEnabled,
    requiredConsentText,
    handleConsentDecisionChange,
    handleConsentTextChange,
    handleSubmit,
    clearValidationError,
    initialize
  };
};

export default useConsentManagement;
